export type PhotoUploadStage =
  | "queued"
  | "compressing"
  | "compressed"
  | "registering"
  | "registered"
  | "reserving"
  | "reserved"
  | "uploading"
  | "uploaded"
  | "attaching"
  | "retry-wait"
  | "failed"
  | "removing";

export interface PhotoUploadQueueJob {
  id: string;
  ownerId: string;
  draftItemId: number;
  role: string;
  stage: PhotoUploadStage;
  sourceBlob: Blob;
  preparedBlob?: Blob;
  thumbnailBlob: Blob;
  fileName: string;
  contentType: string;
  mediaType: string;
  originalFilename: string;
  byteSize: number;
  lastModified: number;
  objectPath?: string;
  uploadUrl?: string;
  reservedAt?: number;
  progress: number;
  attempts: number;
  error?: string;
  recovery?: string;
  retryAt?: number;
  cancelRequested?: boolean;
  leaseOwner?: string;
  leaseExpiresAt?: number;
  createdAt: number;
  updatedAt: number;
}

export interface PhotoUploadQueueView {
  id: string;
  draftItemId: number;
  role: string;
  stage: PhotoUploadStage;
  fileName: string;
  progress: number;
  attempts: number;
  error?: string;
  recovery?: string;
  retryAt?: number;
  objectPath?: string;
  thumbnailUrl: string;
  mediaType: string;
  originalFilename: string;
  byteSize: number;
}

export interface PhotoUploadItemSummary {
  total: number;
  active: number;
  failed: number;
  waiting: number;
  progress: number;
}

export interface EnqueuePhotoInput {
  draftItemId: number;
  role: string;
  file: File;
  clientPhotoId?: string;
}

export function isPdfAttachment(role: string, file: Pick<File, "type">): boolean {
  return file.type.toLowerCase() === "application/pdf" &&
    (role === "paperwork" || role === "misc");
}

export function isSupportedUpload(role: string, file: Pick<File, "type">): boolean {
  const type = file.type.toLowerCase();
  return type.startsWith("image/") || isPdfAttachment(role, file);
}

export function isImageMediaType(mediaType?: string): boolean {
  return !mediaType || mediaType.toLowerCase().startsWith("image/");
}

export interface PhotoUploadQueueApi {
  enqueue(input: EnqueuePhotoInput): Promise<string>;
  retry(jobId: string): Promise<void>;
  remove(jobId: string): Promise<void>;
  retryItem(draftItemId: number): Promise<void>;
  retryItemNow(draftItemId: number): Promise<void>;
  removeItem(draftItemId: number): Promise<void>;
  removeItemAndWait(draftItemId: number): Promise<void>;
  getJobs(): PhotoUploadQueueView[];
  getItemJobs(draftItemId: number): PhotoUploadQueueView[];
  getItemSummary(draftItemId: number): PhotoUploadItemSummary;
  subscribe(listener: () => void): () => void;
  getAvailabilityError(): string | null;
}

interface UploadReservation {
  uploadURL: string;
  objectPath: string;
}

const DB_NAME = "forgebook-photo-upload-queue";
const DB_VERSION = 1;
const STORE_NAME = "jobs";
const OWNER_INDEX = "ownerId";
const MAX_CONCURRENCY = 2;
const LEASE_MS = 35_000;
const MAX_AUTO_ATTEMPTS = 8;
const MAX_RETRY_MS = 5 * 60_000;
const STORAGE_TIMEOUT_MS = 30_000;
const TRANSFER_TIMEOUT_MS = 120_000;
const CANCELLATION_ACK_TIMEOUT_MS = 30_000;
const RESERVATION_REFRESH_MS = 50 * 60_000;

export function isPhotoUploadJobClaimable(
  job: Pick<PhotoUploadQueueJob, "stage" | "retryAt" | "leaseExpiresAt">,
  now: number,
): boolean {
  return (
    job.stage !== "failed" &&
    (!job.retryAt || job.retryAt <= now) &&
    (!job.leaseExpiresAt || job.leaseExpiresAt <= now)
  );
}
class QueueHttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly retryAfterMs?: number,
    readonly retryable = false,
  ) {
    super(message);
    this.name = "QueueHttpError";
  }
}

export class PhotoQueueStorageError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PhotoQueueStorageError";
  }
}

class QueueStoppedError extends Error {
  constructor() {
    super("Photo queue stopped");
    this.name = "QueueStoppedError";
  }
}

class QueueLeaseLostError extends Error {
  constructor() {
    super("Another tab took over this photo job.");
    this.name = "QueueLeaseLostError";
  }
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
  });
}

function storageError(error: unknown): PhotoQueueStorageError {
  const quota =
    error instanceof DOMException &&
    (error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED");
  return new PhotoQueueStorageError(
    quota
      ? "This browser has no space left for queued photos. Free browser storage, then retry or remove a queued photo."
      : "Queued photos cannot be saved in this browser. Keep this tab open, check private-browsing restrictions, or use a supported browser.",
  );
}

async function openQueueDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === "undefined") throw storageError(new Error("unsupported"));
  try {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        const store = database.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex(OWNER_INDEX, "ownerId", { unique: false });
      }
    };
    return await requestToPromise(request);
  } catch (error) {
    throw storageError(error);
  }
}

async function getOwnerJobs(
  database: IDBDatabase,
  ownerId: string,
): Promise<PhotoUploadQueueJob[]> {
  const transaction = database.transaction(STORE_NAME, "readonly");
  const request = transaction
    .objectStore(STORE_NAME)
    .index(OWNER_INDEX)
    .getAll(IDBKeyRange.only(ownerId));
  const jobs = await requestToPromise(request);
  await transactionDone(transaction);
  return jobs.sort((a, b) => a.createdAt - b.createdAt);
}

async function putJob(
  database: IDBDatabase,
  job: PhotoUploadQueueJob,
): Promise<void> {
  try {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(job);
    await transactionDone(transaction);
  } catch (error) {
    throw storageError(error);
  }
}

async function deleteJob(database: IDBDatabase, id: string): Promise<void> {
  const transaction = database.transaction(STORE_NAME, "readwrite");
  transaction.objectStore(STORE_NAME).delete(id);
  await transactionDone(transaction);
}

async function getJob(
  database: IDBDatabase,
  id: string,
): Promise<PhotoUploadQueueJob | undefined> {
  const transaction = database.transaction(STORE_NAME, "readonly");
  const job = await requestToPromise(
    transaction.objectStore(STORE_NAME).get(id),
  );
  await transactionDone(transaction);
  return job;
}

async function clearOwnerJobs(
  database: IDBDatabase,
  ownerId: string,
): Promise<void> {
  const transaction = database.transaction(STORE_NAME, "readwrite");
  const index = transaction.objectStore(STORE_NAME).index(OWNER_INDEX);
  const request = index.openKeyCursor(IDBKeyRange.only(ownerId));
  request.onsuccess = () => {
    const cursor = request.result;
    if (!cursor) return;
    transaction.objectStore(STORE_NAME).delete(cursor.primaryKey);
    cursor.continue();
  };
  await transactionDone(transaction);
}

function canvasBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("Image conversion failed"))),
      type,
      quality,
    );
  });
}

async function resizeImage(
  source: Blob,
  maxEdge: number,
  quality: number,
): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  try {
    const scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const context = canvas.getContext("2d");
    if (!context) throw new Error("Image processing is unavailable");
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return await canvasBlob(canvas, "image/jpeg", quality);
  } finally {
    bitmap.close();
  }
}

async function createThumbnail(source: Blob): Promise<Blob> {
  try {
    return await resizeImage(source, 320, 0.72);
  } catch {
    return source;
  }
}

async function compressPhoto(source: Blob): Promise<Blob> {
  try {
    return await resizeImage(source, 1600, 0.8);
  } catch {
    return source;
  }
}

export function preparedUploadMetadata(
  preparedBlob: Pick<Blob, "size" | "type">,
  fallbackMediaType: string,
): { mediaType: string; byteSize: number } {
  return {
    mediaType: preparedBlob.type || fallbackMediaType,
    byteSize: preparedBlob.size,
  };
}

function parseRetryAfter(response: Response, data: Record<string, unknown>): number | undefined {
  const bodySeconds =
    typeof data.retryAfterSeconds === "number" ? data.retryAfterSeconds : undefined;
  const header = response.headers.get("Retry-After");
  const headerSeconds = header ? Number(header) : undefined;
  const seconds = bodySeconds ?? headerSeconds;
  return seconds && Number.isFinite(seconds) && seconds > 0
    ? seconds * 1_000
    : undefined;
}

async function requireOk(response: Response, fallback: string): Promise<Response> {
  if (response.ok) return response;
  const data = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  const message =
    typeof data.error === "string" && data.error.trim() ? data.error.trim() : fallback;
  throw new QueueHttpError(
    message,
    response.status,
    parseRetryAfter(response, data),
    data.retryable === true,
  );
}

async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted) throw new Error("The request timed out.");
    throw error;
  } finally {
    window.clearTimeout(timeout);
  }
}

async function uploadBlob(
  blob: Blob,
  uploadUrl: string,
  onProgress: (progress: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("PUT", uploadUrl);
    request.timeout = TRANSFER_TIMEOUT_MS;
    request.setRequestHeader("Content-Type", blob.type || "application/octet-stream");
    request.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    };
    request.onloadend = () => {
      if (request.status >= 200 && request.status < 300) resolve();
      else {
        let data: Record<string, unknown> = {};
        try {
          data = JSON.parse(request.responseText) as Record<string, unknown>;
        } catch {
          // Use the transfer fallback below when the response is not JSON.
        }
        const retryAfterSeconds = Number(
          data.retryAfterSeconds ?? request.getResponseHeader("Retry-After"),
        );
        reject(
          new QueueHttpError(
            typeof data.error === "string"
              ? data.error
              : "Photo transfer failed.",
            request.status,
            Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0
              ? retryAfterSeconds * 1_000
              : undefined,
            data.retryable === true,
          ),
        );
      }
    };
    request.onerror = () =>
      reject(new Error("The photo transfer stopped. Check your connection."));
    request.ontimeout = () =>
      reject(new Error("The photo transfer timed out. Check your connection."));
    request.send(blob);
  });
}

function isPermanent(error: unknown): boolean {
  return (
    error instanceof QueueHttpError &&
    error.status >= 400 &&
    error.status < 500 &&
    ![408, 409, 425, 429].includes(error.status)
  );
}

function retryDelay(attempts: number, error: unknown): number {
  if (error instanceof QueueHttpError && error.retryAfterMs) {
    return Math.min(MAX_RETRY_MS, error.retryAfterMs);
  }
  return Math.min(MAX_RETRY_MS, 1_000 * 2 ** Math.min(attempts, 8));
}

export class PhotoUploadQueueController implements PhotoUploadQueueApi {
  private readonly instanceId = crypto.randomUUID();

  private database: IDBDatabase | null = null;

  private jobs: PhotoUploadQueueJob[] = [];

  private views: PhotoUploadQueueView[] = [];

  private readonly urls = new Map<string, string>();

  private readonly listeners = new Set<() => void>();

  private active = 0;

  private stopped = false;

  private wakeTimer: number | undefined;

  private availabilityError: string | null = null;

  constructor(
    private readonly ownerId: string | null,
    private readonly apiBasePath = "/api",
    private readonly storageBasePath = "/api/storage",
  ) {}

  async start(): Promise<void> {
    if (!this.ownerId) return;
    this.stopped = false;
    try {
      this.database = await openQueueDatabase();
      await this.reload();
      window.addEventListener("online", this.wake);
      document.addEventListener("visibilitychange", this.onVisibilityChange);
      this.pump();
    } catch (error) {
      this.availabilityError =
        error instanceof Error ? error.message : "Photo queue is unavailable.";
      this.emit();
    }
  }

  stop(): void {
    this.stopped = true;
    window.removeEventListener("online", this.wake);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    if (this.wakeTimer) window.clearTimeout(this.wakeTimer);
    this.urls.forEach((url) => URL.revokeObjectURL(url));
    this.urls.clear();
    this.database?.close();
    this.database = null;
  }

  async clearLocalData(): Promise<void> {
    if (!this.ownerId) return;
    const database = this.database ?? (await openQueueDatabase());
    await clearOwnerJobs(database, this.ownerId);
    if (!this.database) database.close();
    this.jobs = [];
    this.rebuildViews();
  }

  getAvailabilityError(): string | null {
    return this.availabilityError;
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getJobs = (): PhotoUploadQueueView[] => this.views;

  getItemJobs = (draftItemId: number): PhotoUploadQueueView[] =>
    this.views.filter((job) => job.draftItemId === draftItemId);

  getItemSummary = (draftItemId: number): PhotoUploadItemSummary => {
    const jobs = this.getItemJobs(draftItemId);
    const failed = jobs.filter((job) => job.stage === "failed").length;
    const waiting = jobs.filter((job) => job.stage === "retry-wait").length;
    return {
      total: jobs.length,
      active: jobs.length - failed - waiting,
      failed,
      waiting,
      progress:
        jobs.length === 0
          ? 100
          : Math.round(
              jobs.reduce((total, job) => total + job.progress, 0) / jobs.length,
            ),
    };
  };

  async enqueue(input: EnqueuePhotoInput): Promise<string> {
    if (!this.ownerId) throw new Error("Sign in before adding photos.");
    if (!this.database) {
      throw new PhotoQueueStorageError(
        this.availabilityError ?? "The durable photo queue is still starting. Try again.",
      );
    }
    if (!isSupportedUpload(input.role, input.file)) {
      throw new Error(
        input.file.type.toLowerCase() === "application/pdf"
          ? "PDF attachments are only allowed for Paperwork or Misc."
          : "Only approved image files and Paperwork or Misc PDF attachments are supported.",
      );
    }
    const pdf = isPdfAttachment(input.role, input.file);
    const thumbnailBlob = pdf ? new Blob([], { type: "application/octet-stream" }) : await createThumbnail(input.file);
    const now = Date.now();
    const job: PhotoUploadQueueJob = {
      id: input.clientPhotoId ?? crypto.randomUUID(),
      ownerId: this.ownerId,
      draftItemId: input.draftItemId,
      role: input.role,
      stage: "queued",
      sourceBlob: input.file,
      thumbnailBlob,
      fileName: input.file.name,
      contentType: input.file.type || "application/octet-stream",
      mediaType: input.file.type || "application/octet-stream",
      originalFilename: input.file.name,
      byteSize: input.file.size,
      lastModified: input.file.lastModified,
      progress: 0,
      attempts: 0,
      createdAt: now,
      updatedAt: now,
    };
    await putJob(this.database, job);
    await this.reload();
    this.pump();
    return job.id;
  }

  async retry(jobId: string): Promise<void> {
    const database = this.requireDatabase();
    const job = await getJob(database, jobId);
    if (!job || job.ownerId !== this.ownerId) return;
    const staleReservation =
      Boolean(job.objectPath && job.uploadUrl) &&
      (!job.reservedAt || Date.now() - job.reservedAt >= RESERVATION_REFRESH_MS);
    if (staleReservation) {
      await this.releasePendingObject(job.objectPath!);
      job.objectPath = undefined;
      job.uploadUrl = undefined;
      job.reservedAt = undefined;
      job.stage = "registered";
    } else {
      job.stage = job.objectPath
        ? job.uploadUrl
          ? "reserved"
          : "uploaded"
        : job.preparedBlob
          ? "compressed"
          : "queued";
    }
    job.error = undefined;
    job.recovery = undefined;
    job.retryAt = undefined;
    job.cancelRequested = false;
    job.leaseOwner = undefined;
    job.leaseExpiresAt = undefined;
    job.updatedAt = Date.now();
    await putJob(database, job);
    await this.reload();
    this.pump();
  }

  async remove(jobId: string): Promise<void> {
    const database = this.requireDatabase();
    const job = await getJob(database, jobId);
    if (!job || job.ownerId !== this.ownerId) return;
    job.cancelRequested = true;
    if (!job.leaseExpiresAt || job.leaseExpiresAt <= Date.now()) {
      job.stage = "removing";
      job.leaseOwner = undefined;
      job.leaseExpiresAt = undefined;
    }
    job.updatedAt = Date.now();
    await putJob(database, job);
    await this.reload();
    this.pump();
  }

  async retryItem(draftItemId: number): Promise<void> {
    const jobs = this.jobs.filter(
      (job) => job.draftItemId === draftItemId && job.stage === "failed",
    );
    await Promise.all(jobs.map((job) => this.retry(job.id)));
  }

  async retryItemNow(draftItemId: number): Promise<void> {
    const database = this.requireDatabase();
    const jobs = (await getOwnerJobs(database, this.ownerId!)).filter(
      (job) =>
        job.draftItemId === draftItemId &&
        job.stage !== "removing",
    );
    for (const job of jobs) {
      await this.retry(job.id);
    }
  }

  async removeItem(draftItemId: number): Promise<void> {
    const jobs = this.jobs.filter((job) => job.draftItemId === draftItemId);
    await Promise.all(jobs.map((job) => this.remove(job.id)));
  }

  async removeItemAndWait(draftItemId: number): Promise<void> {
    await this.removeItem(draftItemId);
    const deadline = Date.now() + CANCELLATION_ACK_TIMEOUT_MS;
    const database = this.requireDatabase();

    while (Date.now() < deadline) {
      if (this.stopped) throw new QueueStoppedError();
      const remaining = (await getOwnerJobs(database, this.ownerId!)).filter(
        (job) => job.draftItemId === draftItemId,
      );
      if (remaining.length === 0) {
        await this.reload();
        return;
      }
      const permanentFailure = remaining.find(
        (job) => job.cancelRequested && job.stage === "failed",
      );
      if (permanentFailure) {
        throw new Error(
          permanentFailure.error ??
            "The unfinished photos could not be cancelled.",
        );
      }
      await new Promise((resolve) => window.setTimeout(resolve, 100));
    }

    throw new Error(
      "The unfinished photos are still being cancelled. Try deleting the item again when connected.",
    );
  }

  private requireDatabase(): IDBDatabase {
    if (!this.database) {
      throw new PhotoQueueStorageError(
        this.availabilityError ?? "The durable photo queue is unavailable.",
      );
    }
    return this.database;
  }

  private readonly wake = () => {
    if (this.wakeTimer) {
      window.clearTimeout(this.wakeTimer);
      this.wakeTimer = undefined;
    }
    void this.reload().then(() => this.pump());
  };

  private readonly onVisibilityChange = () => {
    if (document.visibilityState === "visible") this.wake();
  };

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }

  private async reload(): Promise<void> {
    if (!this.database || !this.ownerId || this.stopped) return;
    this.jobs = (await getOwnerJobs(this.database, this.ownerId)).map((job) => ({
      ...job,
      mediaType: job.mediaType ?? (job.contentType || "image/jpeg"),
      originalFilename: job.originalFilename ?? job.fileName,
      byteSize: job.byteSize ?? job.sourceBlob.size,
      thumbnailBlob: job.thumbnailBlob ?? job.sourceBlob,
    }));
    this.rebuildViews();
  }

  private rebuildViews(): void {
    const present = new Set(this.jobs.map((job) => job.id));
    for (const [id, url] of this.urls) {
      if (!present.has(id)) {
        URL.revokeObjectURL(url);
        this.urls.delete(id);
      }
    }
    this.views = this.jobs.map((job) => {
      let thumbnailUrl = this.urls.get(job.id);
      if (!thumbnailUrl) {
        thumbnailUrl = isImageMediaType(job.mediaType)
          ? URL.createObjectURL(job.thumbnailBlob)
          : "";
        this.urls.set(job.id, thumbnailUrl);
      }
      return {
        id: job.id,
        draftItemId: job.draftItemId,
        role: job.role,
        stage: job.stage,
        fileName: job.fileName,
        progress: job.progress,
        attempts: job.attempts,
        error: job.error,
        recovery: job.recovery,
        retryAt: job.retryAt,
        objectPath: job.objectPath,
        thumbnailUrl,
        mediaType: job.mediaType,
        originalFilename: job.originalFilename,
        byteSize: job.byteSize,
      };
    });
    this.emit();
  }

  private pump(): void {
    if (this.stopped || !this.database || !this.ownerId || !navigator.onLine) return;
    while (this.active < MAX_CONCURRENCY) {
      const slot = this.active;
      this.active += 1;
      void this.runInGlobalSlot(slot)
        .then((worked) => {
          this.active -= 1;
          if ((worked || this.hasRunnableJob()) && !this.stopped) this.pump();
        })
        .catch(() => {
          this.active -= 1;
          if (!this.stopped) {
            this.scheduleWake(1_000);
          }
        });
      if (this.active >= MAX_CONCURRENCY) break;
    }
  }

  private async runInGlobalSlot(slot: number): Promise<boolean> {
    if (navigator.locks) {
      return navigator.locks.request(
        `${DB_NAME}:worker:${slot}`,
        () => this.runOne(),
      );
    }
    return this.runOne();
  }

  private hasRunnableJob(): boolean {
    const now = Date.now();
    return this.jobs.some((job) => isPhotoUploadJobClaimable(job, now));
  }

  private async claimNext(): Promise<PhotoUploadQueueJob | null> {
    const database = this.requireDatabase();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const jobs = (await requestToPromise(
      store.index(OWNER_INDEX).getAll(IDBKeyRange.only(this.ownerId!)),
    )) as PhotoUploadQueueJob[];
    const now = Date.now();
    const job = jobs
      .filter(
        (entry) =>
          isPhotoUploadJobClaimable(entry, now),
      )
      .sort((a, b) => a.createdAt - b.createdAt)[0];
    if (!job) {
      await transactionDone(transaction);
      const nextWake = jobs
        .filter((entry) => entry.stage !== "failed")
        .flatMap((entry) => [entry.retryAt, entry.leaseExpiresAt])
        .filter((value): value is number => typeof value === "number" && value > now)
        .sort((a, b) => a - b)[0];
      if (nextWake) this.scheduleWake(nextWake - now);
      return null;
    }
    job.leaseOwner = this.instanceId;
    job.leaseExpiresAt = now + LEASE_MS;
    recoverClaimedPhotoUploadJob(job, now);
    store.put(job);
    await transactionDone(transaction);
    return job;
  }

  private scheduleWake(delay: number): void {
    if (this.wakeTimer) window.clearTimeout(this.wakeTimer);
    this.wakeTimer = window.setTimeout(this.wake, Math.max(100, delay));
  }

  private async runOne(): Promise<boolean> {
    const job = await this.claimNext();
    if (!job) return false;
    const heartbeat = window.setInterval(() => {
      void this.renewLease(job.id).catch(() => {
        // The next compare-and-swap save detects lease loss definitively.
      });
    }, Math.floor(LEASE_MS / 3));
    try {
      if (job.cancelRequested || job.stage === "removing") {
        await this.compensate(job);
        return true;
      }
      await this.process(job);
    } catch (error) {
      if (
        !(error instanceof QueueStoppedError) &&
        !(error instanceof QueueLeaseLostError) &&
        !this.stopped
      ) {
        await this.failOrRetry(job, error);
      }
    } finally {
      window.clearInterval(heartbeat);
      await this.reload();
    }
    return true;
  }

  private async save(job: PhotoUploadQueueJob): Promise<void> {
    if (this.stopped) throw new QueueStoppedError();
    const database = this.requireDatabase();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const persisted = (await requestToPromise(
      store.get(job.id),
    )) as PhotoUploadQueueJob | undefined;
    if (!persisted || persisted.leaseOwner !== this.instanceId) {
      transaction.abort();
      throw new QueueLeaseLostError();
    }
    if (persisted.cancelRequested) job.cancelRequested = true;
    job.updatedAt = Date.now();
    job.leaseOwner = this.instanceId;
    job.leaseExpiresAt = Date.now() + LEASE_MS;
    store.put(job);
    await transactionDone(transaction);
    await this.reload();
  }

  private async renewLease(jobId: string): Promise<void> {
    if (this.stopped || !this.database) return;
    const transaction = this.database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const job = (await requestToPromise(
      store.get(jobId),
    )) as PhotoUploadQueueJob | undefined;
    if (!job || job.leaseOwner !== this.instanceId) {
      transaction.abort();
      return;
    }
    job.leaseExpiresAt = Date.now() + LEASE_MS;
    store.put(job);
    await transactionDone(transaction);
  }

  private async deleteOwnedJob(
    job: PhotoUploadQueueJob,
    cancellationAcknowledged = false,
  ): Promise<"deleted" | "cancelled"> {
    const database = this.requireDatabase();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const persisted = (await requestToPromise(
      store.get(job.id),
    )) as PhotoUploadQueueJob | undefined;
    if (!persisted || persisted.leaseOwner !== this.instanceId) {
      transaction.abort();
      throw new QueueLeaseLostError();
    }
    if (persisted.cancelRequested && !cancellationAcknowledged) {
      await transactionDone(transaction);
      job.cancelRequested = true;
      return "cancelled";
    }
    store.delete(job.id);
    await transactionDone(transaction);
    return "deleted";
  }

  private async process(job: PhotoUploadQueueJob): Promise<void> {
    if (job.stage === "queued") {
      job.stage = isImageMediaType(job.mediaType) ? "compressing" : "compressed";
      job.progress = 5;
      await this.save(job);
      job.preparedBlob = isImageMediaType(job.mediaType)
        ? await compressPhoto(job.sourceBlob)
        : job.sourceBlob;
      const preparedMetadata = preparedUploadMetadata(
        job.preparedBlob,
        job.mediaType,
      );
      job.mediaType = preparedMetadata.mediaType;
      job.contentType = preparedMetadata.mediaType;
      job.byteSize = preparedMetadata.byteSize;
      job.stage = "compressed";
      job.progress = 15;
      await this.save(job);
    }
    if (job.cancelRequested) return this.compensate(job);

    if (job.stage === "compressed") {
      job.stage = "registering";
      await this.save(job);
      await requireOk(
        await fetchWithTimeout(
          `${this.apiBasePath}/item-drafts/${job.draftItemId}/photos/${encodeURIComponent(job.id)}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              role: job.role,
              mediaType: job.mediaType,
              originalFilename: job.originalFilename,
              byteSize: job.byteSize,
            }),
          },
          STORAGE_TIMEOUT_MS,
        ),
        "The attachment could not be registered.",
      );
      job.stage = "registered";
      job.progress = 20;
      await this.save(job);
    }
    if (job.cancelRequested) return this.compensate(job);

    if (job.stage === "registered") {
      job.stage = "reserving";
      await this.save(job);
      const response = await requireOk(
        await fetchWithTimeout(
          `${this.storageBasePath}/uploads/request-url`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: job.fileName,
              size: job.preparedBlob!.size,
              contentType: job.contentType,
              role: job.role,
            }),
          },
          STORAGE_TIMEOUT_MS,
        ),
        "The upload could not be prepared.",
      );
      const reservation = (await response.json()) as UploadReservation;
      job.uploadUrl = reservation.uploadURL;
      job.objectPath = reservation.objectPath;
      job.reservedAt = Date.now();
      job.stage = "reserved";
      job.progress = 25;
      await this.save(job);
    }
    if (job.cancelRequested) return this.compensate(job);

    if (job.stage === "reserved") {
      if (
        !job.reservedAt ||
        Date.now() - job.reservedAt >= RESERVATION_REFRESH_MS
      ) {
        await this.resetReservation(job);
        return;
      }
      job.stage = "uploading";
      await this.save(job);
      let lastSaved = 0;
      let progressWrite = Promise.resolve();
      try {
        await uploadBlob(job.preparedBlob!, job.uploadUrl!, (transferProgress) => {
          job.progress = 25 + Math.round(transferProgress * 0.65);
          const now = Date.now();
          if (now - lastSaved > 4_000) {
            lastSaved = now;
            progressWrite = progressWrite
              .then(() => this.save(job))
              .catch(() => {});
          }
        });
      } catch (error) {
        if (
          error instanceof QueueHttpError &&
          [404, 410].includes(error.status)
        ) {
          await progressWrite;
          await this.resetReservation(job);
          return;
        }
        throw error;
      }
      if (job.stage === "uploading") {
        await progressWrite;
        job.stage = "uploaded";
        job.progress = 90;
        job.uploadUrl = undefined;
        job.reservedAt = undefined;
        await this.save(job);
      }
    }
    if (job.cancelRequested) return this.compensate(job);

    if (job.stage === "uploaded") {
      try {
        await requireOk(
          await fetchWithTimeout(`${this.storageBasePath}/uploads/finalize`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ objectPath: job.objectPath }),
          }, STORAGE_TIMEOUT_MS),
          "The uploaded attachment could not be verified.",
        );
      } catch (error) {
        if (
          error instanceof QueueHttpError &&
          [404, 410].includes(error.status)
        ) {
          await this.resetReservation(job);
          return;
        }
        throw error;
      }
    }
    if (job.cancelRequested) return this.compensate(job);

    if (job.stage === "uploaded") {
      job.stage = "attaching";
      await this.save(job);
      await requireOk(
        await fetchWithTimeout(
          `${this.apiBasePath}/item-drafts/${job.draftItemId}/photos/${encodeURIComponent(job.id)}/attach`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ role: job.role, objectPath: job.objectPath }),
          },
          STORAGE_TIMEOUT_MS,
        ),
        "The uploaded attachment could not be attached.",
      );
      job.progress = 100;
      await this.save(job);
      if (
        job.cancelRequested ||
        (await this.deleteOwnedJob(job)) === "cancelled"
      ) {
        await this.compensate(job);
      }
    }
  }

  private async compensate(job: PhotoUploadQueueJob): Promise<void> {
    if (this.stopped) throw new QueueStoppedError();
    const unregister = await fetch(
      `${this.apiBasePath}/item-drafts/${job.draftItemId}/photos/${encodeURIComponent(job.id)}`,
      { method: "DELETE" },
    );
    if (!unregister.ok && unregister.status !== 404) {
      throw new QueueHttpError(
        "The photo is still being removed. Retry when connected.",
        unregister.status,
      );
    }
    if (job.objectPath) {
      await this.releasePendingObject(job.objectPath);
    }
    await this.deleteOwnedJob(job, true);
  }

  private async releasePendingObject(objectPath: string): Promise<void> {
    const release = await fetch(`${this.storageBasePath}/uploads/pending`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ objectPath }),
    });
    if (!release.ok && release.status !== 404 && release.status !== 409) {
      throw new QueueHttpError("Unused upload cleanup is still pending.", release.status);
    }
  }

  private async resetReservation(job: PhotoUploadQueueJob): Promise<void> {
    if (job.objectPath) await this.releasePendingObject(job.objectPath);
    job.objectPath = undefined;
    job.uploadUrl = undefined;
    job.reservedAt = undefined;
    job.stage = "registered";
    job.progress = 20;
    await this.save(job);
  }

  private async failOrRetry(
    job: PhotoUploadQueueJob,
    error: unknown,
  ): Promise<void> {
    const database = this.requireDatabase();
    const transaction = database.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const current = (await requestToPromise(
      store.get(job.id),
    )) as PhotoUploadQueueJob | undefined;
    if (
      !current ||
      current.ownerId !== this.ownerId ||
      current.leaseOwner !== this.instanceId
    ) {
      await transactionDone(transaction);
      return;
    }
    const disposition = photoUploadRetryDisposition({
      attempts: current.attempts,
      status: error instanceof QueueHttpError ? error.status : undefined,
      retryable: error instanceof QueueHttpError && error.retryable,
    });
    current.attempts = disposition.attempts;
    current.error = error instanceof Error ? error.message : "Photo upload failed.";
    current.leaseOwner = undefined;
    current.leaseExpiresAt = undefined;
    const permanent = disposition.failed;
    if (permanent) {
      current.stage = "failed";
      current.retryAt = undefined;
      current.recovery =
        error instanceof QueueHttpError &&
        error.status === 400 &&
        /limit reached/i.test(error.message)
          ? "Remove this failed upload and the photo already occupying this slot before selecting another."
          : isPermanent(error)
            ? "Remove this upload, correct the item, then select the file again."
            : "Automatic retries stopped. Check your connection, then tap retry.";
    } else {
      const delay = retryDelay(current.attempts, error);
      current.stage = "retry-wait";
      current.retryAt = Date.now() + delay;
      current.recovery = navigator.onLine
        ? `Retrying automatically in ${Math.ceil(delay / 1_000)} seconds.`
        : "Waiting for an internet connection.";
      this.scheduleWake(delay);
    }
    current.updatedAt = Date.now();
    store.put(current);
    await transactionDone(transaction);
  }
}

export function recoverClaimedPhotoUploadJob(
  job: PhotoUploadQueueJob,
  now: number,
): void {
  if (job.stage === "compressing") job.stage = "queued";
  if (job.stage === "registering") job.stage = "compressed";
  if (job.stage === "reserving") job.stage = "registered";
  if (job.stage === "uploading") job.stage = "reserved";
  if (job.stage === "attaching") job.stage = "uploaded";
  if (job.stage === "retry-wait") {
    const staleReservation =
      Boolean(job.objectPath && job.uploadUrl) &&
      (!job.reservedAt || now - job.reservedAt >= RESERVATION_REFRESH_MS);
    job.stage = staleReservation
      ? "reserved"
      : job.objectPath
        ? job.uploadUrl
          ? "reserved"
          : "uploaded"
        : job.preparedBlob
          ? "compressed"
          : "queued";
    job.retryAt = undefined;
  }
}

export function photoUploadRetryDisposition({
  attempts,
  status,
  retryable,
}: {
  attempts: number;
  status?: number;
  retryable: boolean;
}): { attempts: number; failed: boolean } {
  const nextAttempts = attempts + (retryable ? 0 : 1);
  const permanentStatus =
    typeof status === "number" &&
    status >= 400 &&
    status < 500 &&
    ![408, 409, 425, 429].includes(status);
  return {
    attempts: nextAttempts,
    failed: permanentStatus || (!retryable && nextAttempts >= MAX_AUTO_ATTEMPTS),
  };
}
